import { useState, useEffect } from "react";
import { ShieldCheck, Loader2, MailCheck, KeyRound, AlertCircle } from "lucide-react";
import { startVerification, confirmVerificationCode, requestManualReview, getVerifyStatus } from "./verifyClient";

// Anti-fraud pre-purchase panel. Sits above the Membership Tier block on the
// setup form. The rest of the form stays visually greyed out until this
// component reports verify_token via onVerified().
//
// Sector-specific labels are passed in as `sectorLabel`. If the URL already
// contains a `?token=` (manual approval email link) we skip straight to
// verified.
export default function OrgVerification({ sector, sectorLabel, onVerified }) {
  const [phase, setPhase] = useState("form"); // form | code_sent | verified | manual_pending | manual_approved
  const [orgName, setOrgName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [verifyId, setVerifyId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Cover the manual-approval email link ?token=...&email=... path
  useEffect(() => {
    const usp = new URLSearchParams(window.location.search);
    const t = usp.get("token");
    const emailQ = usp.get("email") || "";
    if (t && t.startsWith("vt_")) {
      onVerified && onVerified(t, emailQ);
      if (emailQ) setEmail(emailQ);
      setPhase("verified");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async (e) => {
    e && e.preventDefault();
    setErr("");
    if (!orgName.trim() || !email.trim()) {
      setErr("Please enter both your organisation name and official email.");
      return;
    }
    setBusy(true);
    try {
      const r = await startVerification(orgName.trim(), email.trim(), sector, marketingConsent);
      setVerifyId(r.verify_id);
      if (r.status === "code_sent") setPhase("code_sent");
      else setPhase("manual_pending");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  const confirm = async (e) => {
    e && e.preventDefault();
    setErr("");
    if (!/^\d{6}$/.test(code.trim())) { setErr("Please enter the 6-digit code from your email."); return; }
    setBusy(true);
    try {
      const token = await confirmVerificationCode(verifyId, code.trim());
      setPhase("verified");
      onVerified && onVerified(token, email.trim());
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "That code didn't match.");
    } finally { setBusy(false); }
  };

  const askManual = async () => {
    setErr(""); setBusy(true);
    try {
      await requestManualReview(verifyId);
      setPhase("manual_pending");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not send request.");
    } finally { setBusy(false); }
  };

  // Poll manual approval so the applicant can just wait on the page
  useEffect(() => {
    if (phase !== "manual_pending" || !verifyId) return;
    const id = setInterval(async () => {
      try {
        const s = await getVerifyStatus(verifyId);
        if (s.verify_token) {
          setPhase("verified");
          onVerified && onVerified(s.verify_token, email.trim());
          clearInterval(id);
        }
      } catch {}
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, verifyId]);

  const bx = "bg-white rounded-2xl border-2 border-[#E8E5DC] p-4";
  const fld = "w-full px-3 py-2 rounded-xl border-2 border-[#E0E0E0] focus:border-[#B89823] outline-none text-sm";

  return (
    <div className={bx} data-testid="org-verification-panel">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-5 h-5 text-[#B89823]" />
        <p className="text-xs font-semibold uppercase tracking-wide text-[#4A4A4A]">Organisation Verification</p>
      </div>
      {phase === "form" && (
        <>
          <p className="text-xs text-[#6B6B6B] mb-3">Enter your official organisation email to verify your eligibility and continue. We'll check your organisation and send a verification code to this address.</p>
          <form onSubmit={start} className="space-y-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#4A4A4A] mb-1">{sectorLabel} name *</label>
              <input value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="orgverify-name" className={fld} placeholder={sector === "hotel" ? "e.g. The Grand Ocean Hotel Ltd." : sector === "school" ? "e.g. Sunnyside Primary School" : "e.g. Rainbow Kids Charity"} />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#4A4A4A] mb-1">Official {sectorLabel.toLowerCase()} email *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="orgverify-email" className={fld} placeholder="you@yourorganisation.uk" />
            </div>
            {err && <div className="bg-[#FFF0F0] border border-[#F5D0D0] rounded-xl p-2 text-xs text-[#A55B64]" data-testid="orgverify-error">{err}</div>}
            <label className="flex items-start gap-2 cursor-pointer select-none text-[11px] leading-relaxed text-[#4A4A4A]" data-testid="orgverify-marketing-consent-label">
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                data-testid="orgverify-marketing-consent"
                className="mt-0.5 accent-[#B89823] w-3.5 h-3.5"
              />
              <span>I would like to receive Poodle Talk news, product updates and resources by email. I can unsubscribe at any time.</span>
            </label>
            <button type="submit" disabled={busy} data-testid="orgverify-start" className="w-full px-4 py-2.5 bg-[#4A4A4A] text-white rounded-full font-semibold text-sm hover:bg-black disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {busy ? "Checking…" : "Verify organisation"}
            </button>
          </form>
        </>
      )}

      {phase === "code_sent" && (
        <>
          <p className="text-xs text-[#6B6B6B] mb-3 flex items-start gap-1.5"><MailCheck className="w-4 h-4 mt-0.5 text-[#B89823]" /> A 6-digit code has been sent to <strong>{email}</strong>. It expires in 15 minutes.</p>
          <form onSubmit={confirm} className="space-y-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#4A4A4A] mb-1">Enter code *</label>
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} inputMode="numeric" data-testid="orgverify-code" placeholder="123456" className={fld + " text-center font-mono tracking-widest text-lg"} />
            </div>
            {err && <div className="bg-[#FFF0F0] border border-[#F5D0D0] rounded-xl p-2 text-xs text-[#A55B64]" data-testid="orgverify-error">{err}</div>}
            <button type="submit" disabled={busy} data-testid="orgverify-confirm" className="w-full px-4 py-2.5 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold text-sm hover:bg-[#D9C599] disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              {busy ? "Confirming…" : "Confirm code"}
            </button>
            <button type="button" disabled={busy} onClick={askManual} data-testid="orgverify-ask-manual" className="w-full text-xs text-[#6B6B6B] hover:text-[#4A4A4A] underline underline-offset-2">
              I didn't get the code — request manual approval
            </button>
          </form>
        </>
      )}

      {phase === "manual_pending" && (
        <div className="bg-[#FFF7D6] border border-[#E8D4A8] rounded-xl p-3 text-xs text-[#5A4A32]" data-testid="orgverify-manual-pending">
          <p className="font-semibold flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> We couldn't verify this organisation automatically.</p>
          <p className="mt-1">Please check your details or wait a short while — a member of our team will manually review your request and email you a continuation link. This page checks for approval every 15 seconds.</p>
        </div>
      )}

      {phase === "verified" && (
        <div className="bg-[#E8F5E8] border-2 border-[#B6DBB6] rounded-xl p-3 text-sm text-[#2F6B2F] font-semibold" data-testid="orgverify-verified">
          ✓ Organisation verified — you can now select your membership and continue.
        </div>
      )}
    </div>
  );
}
